import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FieldSeparator } from '@bliks-io/ui';
import { SystemSuggestionPayloadSchema, type SystemSuggestionPayload } from '@bliks-io/contracts';
import { ENTITY_ICONS } from '@/constants/entityIcons';
import { FormInput, FormTextarea } from '@/components/form/rhf';
import { EntitySuggestionDialog } from './EntitySuggestionDialog';
import type { SuggestionPickEvent } from './shared/suggestionPickEvent';

export interface SystemSuggestionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialName: string;
  /**
   * Source-link refinement: exactly one of these must be set so the backend
   * can attach the suggestion to its originating recording or process.
   */
  sourceRecordingId?: string;
  /** Process behind a node-anchored source — cache-seed hint, not wire data. */
  seedProcessId?: string;
  /** Draft-editor origin: create/link post to the draft suggestion routes. */
  draftProcessId?: string;
  /**
   * Called when the user picks an existing system from the duplicate panel.
   * Allows the parent picker to swap in the existing entity instead.
   */
  onUseExistingEntity?: (entityId: string) => void;
  /** Called after a suggestion is created or adopted (discriminated on `kind`). */
  onSuggestionCreated?: (event: SuggestionPickEvent) => void;
}

export function SystemSuggestionDialog({
  open,
  onOpenChange,
  initialName,
  sourceRecordingId,
  seedProcessId,
  draftProcessId,
  onUseExistingEntity,
  onSuggestionCreated
}: SystemSuggestionDialogProps) {
  const { t } = useTranslation();

  const defaultValues = useMemo<SystemSuggestionPayload>(
    () => ({
      name: initialName,
      description: '',
      category: '',
      url: ''
    }),
    [initialName]
  );

  return (
    <EntitySuggestionDialog<SystemSuggestionPayload>
      open={open}
      onOpenChange={onOpenChange}
      sourceRecordingId={sourceRecordingId}
      seedProcessId={seedProcessId}
      draftProcessId={draftProcessId}
      onUseExistingEntity={onUseExistingEntity}
      onSuggestionCreated={onSuggestionCreated}
      kind="system"
      icon={ENTITY_ICONS.system}
      titleKey="entitySuggestion.system.dialog.title"
      descriptionKey="entitySuggestion.system.dialog.description"
      submitLabelKey="entitySuggestion.system.dialog.submit"
      schema={SystemSuggestionPayloadSchema}
      defaultValues={defaultValues}
      primaryFieldName="name"
      secondaryFieldName="category"
      buildCandidatesPayload={({ name, secondaryValue }) => ({
        name,
        category: secondaryValue
      })}
      toApiPayload={(data) => ({
        name: data.name.trim(),
        description: data.description?.trim() || undefined,
        category: data.category?.trim() || undefined,
        url: data.url?.trim() || undefined
      })}
      duplicateCandidatesPanelKind="system"
      renderFields={({ form, candidatesPanel }) => (
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormInput
                control={form.control}
                name="name"
                label={t('entitySuggestion.system.dialog.name')}
                required
              />
              <FormInput
                control={form.control}
                name="category"
                label={t('entitySuggestion.system.dialog.category')}
                labelHint={t('entitySuggestion.system.dialog.categoryHint')}
              />
            </div>
            {candidatesPanel}
          </div>

          <div className="flex flex-col gap-4">
            <FieldSeparator>{t('common.Details')}</FieldSeparator>
            <FormTextarea
              control={form.control}
              name="description"
              label={t('entitySuggestion.system.dialog.description_field')}
              rows={3}
            />
            <FormInput
              control={form.control}
              name="url"
              label={t('entitySuggestion.system.dialog.url')}
            />
          </div>
        </div>
      )}
    />
  );
}
